import { useLocation } from 'react-router-dom';
import { debounceAsync } from 'utils/debounce';
import { Dispatch, FC, SetStateAction, useMemo, useRef, useState } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { Drawer, Form, FormInstance, Input, Modal, Skeleton } from 'antd';
import { TbBuilding } from 'react-icons/tb';
import AddContactModal from 'modules/client/components/AddContactModal';
import ErrorResult from 'components/ErrorResult';
import asyncErrorHandler from 'utils/asyncErrorHandler';
import apiRequests from 'utils/api';
import apiRoutes from 'config/apiRoute';
import ClientForm from './components/ClientForm';
import ProjectsCard from './components/ProjectsCard';
import PendingInvitesCard from './components/PendingInvitesCard';
import ContactsCard from './components/ContactsCard';
import RichTextEditor, { RichTextEditorHandle } from '../RichTextEditor';
import { UploadFileApi } from '../../types/upload-type';

const projectStatus = ['paused', 'waiting_to_launch', 'in_progress', 'completed', 'cancelled', 'active'];

interface ClientCardProps {
  form: FormInstance;
  onFormUpdate: (changedValues: Record<string, any>, allValues: Record<string, any>) => Promise<unknown>;
  client?: Record<string, any>;
  projects?: Record<string, any>[];
  contacts?: Record<string, any>[];
  pendingInvites?: Record<string, any>[];
  editing: boolean;
  showAllProjects?: boolean;
  setNoteImages: Dispatch<SetStateAction<UploadFileApi[]>>;
  setLoadingImages: Dispatch<SetStateAction<number>>;
  onShowAllProjects?: (show: boolean) => Promise<void>;
  onFetchPendingInvites?: () => Promise<void>;
  onFetchContacts?: () => Promise<void>;
  onToggleStrictRequests?: (contact: any, value: boolean) => Promise<void>;
}

const ClientCard: FC<ClientCardProps> = ({
  form,
  onFormUpdate,
  client,
  projects,
  contacts,
  pendingInvites,
  editing,
  showAllProjects,
  setNoteImages,
  setLoadingImages,
  onShowAllProjects,
  onFetchPendingInvites,
  onFetchContacts,
  onToggleStrictRequests,
}) => {
  const location = useLocation();
  const isClientsPage = location.pathname === '/clients';

  const [visible, setVisible] = useState(null as null | 'new' | 'resend-contact' | 'resend-pending');
  const [modalData, setModalData] = useState(null as any);
  const [fetchingPendingInvites, setFetchingPendingInvites] = useState(false);
  const [fetchingContacts, setFetchingContacts] = useState(false);

  const [openNotesModal, setOpenNotesModal] = useState(false);
  const editorModalRef = useRef<RichTextEditorHandle>(null);
  const editorPageRef = useRef<RichTextEditorHandle>(null);

  const fetchPendingInvites = () => {
    if (onFetchPendingInvites) {
      setFetchingPendingInvites(true);
      onFetchPendingInvites().finally(() => setFetchingPendingInvites(false));
    }
  };

  const fetchContacts = () => {
    if (onFetchContacts) {
      setFetchingContacts(true);
      onFetchContacts().finally(() => setFetchingContacts(false));
    }
  };

  const onDeleteContactHandler = async (contact: any) => {
    try {
      await apiRequests.put(`${apiRoutes.CONTACTS}/${contact.uuid}`, {
        customers: [...contact.customers?.filter((el: any) => el.uuid !== client?.uuid)?.map((el: any) => el.uuid)],
        status: contact.status,
        type: contact.type,
        customer_id: contact.customer?.uuid,
      });

      fetchContacts();
    } catch (error) {
      asyncErrorHandler(error);
    }
  };

  const onDeletePendingInviteHandler = async (invite: any) => {
    try {
      await apiRequests.put(`${apiRoutes.CUSTOMER_INVITE}/${invite.uuid}`, {
        status: 'cancelled',
      });

      fetchPendingInvites();
    } catch (error) {
      asyncErrorHandler(error);
    }
  };

  if (!client) {
    return (
      <div style={{ padding: '16px 22px' }}>
        <ClientForm form={form} onFormUpdate={onFormUpdate} />
      </div>
    );
  }

  return (
    <div style={{ overflowY: 'auto', padding: '18px', height: '100%' }}>
      {visible && (
        <AddContactModal
          visible={visible}
          projectUuid={projects?.[0]?.uuid}
          clientUuid={client.uuid}
          modalData={modalData}
          pendingInvites={pendingInvites}
          onFinish={() => {
            fetchPendingInvites();
            fetchContacts();
          }}
          onClose={() => {
            setVisible(null);
            setModalData(null);
          }}
        />
      )}

      <Modal
        visible={openNotesModal}
        className="ant-modal-outside-close"
        width="100%"
        bodyStyle={{ padding: '24px 0' }}
        style={{ maxWidth: '1012px' }}
        onCancel={() => {
          if (editorModalRef.current && editorPageRef.current) {
            editorPageRef.current.updateValue(editorModalRef.current.getValue() ?? '');
          }
          setOpenNotesModal(false);
        }}
        footer={null}
        forceRender
      >
        <Form colon={false} form={form} onValuesChange={onFormUpdate}>
          <Form.Item name="notes" noStyle>
            <RichTextEditor
              className="min-h"
              initialValue={client.notes_delta ?? client.notes}
              progressBar="bottom"
              onLoadingImage={setLoadingImages}
              onImageUploader={async (upload) => {
                setNoteImages((old) => [...old, upload]);
              }}
              onChange={(html, delta, source, editor) => {
                const deltaJson = JSON.stringify(editor.getContents());
                form.setFieldsValue({
                  notes: html,
                  notes_delta: deltaJson,
                });
              }}
            />
          </Form.Item>
          <Form.Item name="notes_delta" noStyle>
            <Input type="hidden" />
          </Form.Item>
        </Form>
      </Modal>

      <ClientForm form={form} onFormUpdate={onFormUpdate} client={client} editing={editing} />

      <div className="w-full" style={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>
        <span style={{ color: 'rgba(0, 68, 204, 0.75)', marginBottom: 6 }}>Notes:</span>
        <RichTextEditor
          key={client.notes_delta ?? client.notes ?? client.uuid}
          ref={editorPageRef}
          className="h-middle no-toolbar"
          initialValue={client.notes_delta ?? client.notes}
          placeholder="Project notes"
          readonly
          showMoreButton
        />

        <div
          style={{ position: 'absolute', height: '100%', width: '100%', top: 0, cursor: 'pointer' }}
          onClick={() => setOpenNotesModal(true)}
        />
      </div>

      {projects && isClientsPage && (
        <ProjectsCard projects={projects} showAllProjects={showAllProjects} onShowAllProjects={onShowAllProjects} />
      )}

      {contacts && (
        <ContactsCard
          contacts={contacts}
          fetching={fetchingContacts}
          clientUuid={client?.uuid}
          onInvite={() => setVisible('new')}
          onDeleteContact={onDeleteContactHandler}
          onToggleStrictRequests={onToggleStrictRequests}
          onResendInvite={(contact) => {
            setVisible('resend-contact');
            setModalData(contact);
          }}
        />
      )}

      {pendingInvites && (
        <PendingInvitesCard
          pendingInvites={pendingInvites}
          fetching={fetchingPendingInvites}
          onDeletePendingInvite={onDeletePendingInviteHandler}
          onResendPendingInvite={(invite) => {
            setVisible('resend-pending');
            setModalData(invite);
          }}
        />
      )}
    </div>
  );
};

interface ClientModalBodyProps {
  uuid: string;
  onSaved?: (data: Record<string, any>) => void;
}

const ClientModalBody: FC<ClientModalBodyProps> = ({ uuid, onSaved }) => {
  const isCreate = uuid === 'create';

  const [client, setClient] = useState<Record<string, any>>();
  const [noteImages, setNoteImages] = useState<UploadFileApi[]>([]);
  const [projects, setProjects] = useState<Record<string, any>[]>();
  const [contacts, setContacts] = useState<Record<string, any>[]>();
  const [pendingInvites, setPendingInvites] = useState<Record<string, any>[]>();
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [loadingImages, setLoadingImages] = useState(0);
  const [clientError, setClientError] = useState<any>(null);
  const [fetching, setFetching] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const fetchedRef = useRef(false);

  const [form] = Form.useForm();

  const onFormUpdate = useMemo(
    () =>
      debounceAsync(async (changedValues: Record<string, any>, allValues: Record<string, any>) => {
        if (!client) return;

        setSaving(true);

        try {
          const payload: Record<string, any> = { ...changedValues };

          if (changedValues.notes) {
            const { notes } = allValues;
            const uploads = notes ? noteImages.filter((x) => x.url && notes.includes(`src="${x.url}"`)) : [];

            payload.uploads = uploads.length === 0 ? undefined : uploads.map((x) => x.uid);
          }

          const response = await apiRequests.put(`${apiRoutes.CUSTOMERS}/${client.uuid}`, payload);

          setClient((prev) => ({
            ...prev,
            ...(response.data?.data ?? {}),
          }));

          if (changedValues.notes) {
            setNoteImages((old) => old.filter((x) => x.url && !payload.uploads?.includes(x.uid)));
          }

          onSaved?.(response.data.data);
        } catch (error) {
          asyncErrorHandler(error);
        } finally {
          setSaving(false);
        }
      }, 800),
    [client, noteImages, onSaved],
  );

  const fetchClient = async () => {
    const response = await apiRequests.get(`${apiRoutes.CUSTOMERS}/${uuid}`);

    setClient(response.data.data);
  };

  const fetchProjects = async (showAll: boolean) => {
    const response = await apiRequests.get(apiRoutes.PROJECTS, {
      'page[size]': 300,
      'filters[customer.uuid][]': uuid,
      'filters[projects.status][]': showAll
        ? projectStatus
        : projectStatus.filter((value) => !['cancelled', 'completed'].includes(value)),
    });

    setProjects(response.data.data);
  };

  const fetchContacts = async () => {
    const response = await apiRequests.get(apiRoutes.CONTACTS, {
      'page[size]': 300,
      'filters[customer.uuid][]': uuid,
      'filters[billing_contacts.status][]': 'active',
    });

    setContacts(response.data.data);
  };

  const onToggleStrictRequests = async (contact: any, value: boolean) => {
    try {
      await apiRequests.put(`${apiRoutes.CONTACTS}/${contact.uuid}/customers/${uuid}`, {
        strict_requests: value,
      });
    } catch (error) {
      asyncErrorHandler(error);
    }
  };

  const fetchPendingInvites = async () => {
    const response = await apiRequests.get(apiRoutes.CUSTOMER_INVITE, {
      'page[size]': 300,
      'filters[status][]': 'pending',
      'filters[customer.uuid][]': uuid,
    });

    setPendingInvites(response.data.data);
  };

  const fetchAll = () => {
    setFetching(true);

    Promise.all([fetchClient(), fetchProjects(showAllProjects), fetchContacts(), fetchPendingInvites()])
      .then(() => {
        setClientError(null);
      })
      .catch((error) => {
        setClientError(error);
      })
      .then((error) => {
        setClientError(error);
      })
      .finally(() => {
        setFetching(false);
      });
  };

  const onShowAllProjectsHandler = (showAll: boolean) => {
    setShowAllProjects(showAll);
    return fetchProjects(showAll);
  };

  if (!fetchedRef.current && !isCreate) {
    fetchedRef.current = true;
    fetchAll();
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '14px 18px',
          borderBottom: '1px solid #0044CC33',
          paddingRight: 70,
        }}
      >
        <div className="primary" style={{ display: 'flex', alignItems: 'center' }}>
          <TbBuilding style={{ fontSize: 24, marginRight: 6 }} />
          <span style={{ fontSize: 18 }}>{isCreate ? 'New client' : 'Client details'}</span>
        </div>

        {saving && <LoadingOutlined style={{ fontSize: 22, marginTop: 4, color: '#0044CC' }} spin />}
      </div>

      {fetching && (
        <div style={{ padding: '6px 18px' }}>
          <Skeleton loading />
        </div>
      )}

      {!fetching && clientError ? (
        <div style={{ padding: '6px 18px' }}>
          <ErrorResult error={clientError} />
        </div>
      ) : undefined}

      {!fetching && !clientError && (
        <ClientCard
          form={form}
          onFormUpdate={onFormUpdate}
          editing
          client={client}
          projects={projects}
          contacts={contacts}
          pendingInvites={pendingInvites}
          showAllProjects={showAllProjects}
          setNoteImages={setNoteImages}
          setLoadingImages={setLoadingImages}
          onShowAllProjects={onShowAllProjectsHandler}
          onFetchContacts={fetchContacts}
          onFetchPendingInvites={fetchPendingInvites}
          onToggleStrictRequests={onToggleStrictRequests}
        />
      )}
    </div>
  );
};

ClientModalBody.displayName = 'ClientModalBody';

interface ClientModalProps {
  uuid: string | null;
  onClose?: () => void;
  onSaved?: (data: Record<string, any>) => void;
}

const ClientModal: FC<ClientModalProps> = ({ uuid, onClose, onSaved }) => {
  const uuidRef = useRef<string | null>(null);

  if (uuidRef.current !== uuid && uuid) {
    uuidRef.current = uuid;
  }

  return (
    <Drawer
      visible={!!uuid}
      width={640}
      footer={null}
      bodyStyle={{
        padding: '0 0 16px 0',
      }}
      onClose={onClose}
      destroyOnClose
      maskClosable
    >
      {uuidRef.current ? <ClientModalBody key={uuidRef.current} uuid={uuidRef.current} onSaved={onSaved} /> : undefined}
    </Drawer>
  );
};

export default ClientModal;
